import { FileTextIcon, PlusIcon } from "lucide-react";

import { OldEvaluationForm } from "./OldEvaluationForm";
import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useGetCandidacyEvaluations } from "@/hooks/queries/useGetCandidacyEvaluations";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { useSetCandidacyEvaluations } from "@/hooks/sets/useSetCandidacyEvaluations";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  CandidacyEvaluationBlock,
  EvaluationBlockType,
  EvaluationBuild,
  EvaluationScoringType,
} from "@/types";

// The legacy evaluations tab, shown in place of the V2 evaluation on jobs whose
// internal config still carries evaluation builds.
export const OldEvaluationsTab = ({
  candidacyId,
}: {
  candidacyId: string;
}) => {
  /* Context */
  const { contentPaddingClassName, jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const evaluationsQuery = useGetCandidacyEvaluations({ candidacyId });
  const jobInternalConfig = useGetJobInternalConfig().data;
  const profile = useGetCandidacySheetData({ jobId, userId: workerId }).data;
  // Update
  const { mutate: updateCandidacy } = useUpdateCandidacy();
  const setCandidacyEvaluations = useSetCandidacyEvaluations();

  /* Helpers */
  const evaluations = evaluationsQuery.data;
  const jobEvaluations = jobInternalConfig?.evaluations;

  /* Handlers */
  const handleCreateEvaluation = (evaluation?: EvaluationBuild) => {
    const fullName = `${profile?.profile.firstName ?? ""} ${profile?.profile.lastName ?? ""}`;
    const competencyBlocks: CandidacyEvaluationBlock[] = evaluation?.blocks
      ? evaluation.blocks.map((block) => ({
          ...block,
          notes: null,
          score:
            block.scoringType === EvaluationScoringType.COMPLETION
              ? false
              : null,
          questions: block.questions.map((question) => ({
            ...question,
            answer:
              block.blockType === EvaluationBlockType.STRUCTURED
                ? false
                : undefined,
          })),
        }))
      : [];

    updateCandidacy(
      {
        candidacyId,
        updateCandidacyRequest: {
          evaluations: [
            {
              id: crypto.randomUUID(),
              blocks: competencyBlocks,
              createdAt: new Date().toISOString(),
              evaluationScore: null,
              evaluationSummary: null,
              miscNotes: null,
              name: `${fullName}'s Evaluation`,
            },
          ],
        },
      },
      {
        onSuccess: (candidacy) => {
          setCandidacyEvaluations({
            candidacyId,
            evaluations: candidacy.evaluations,
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    if (evaluationsQuery.isPending) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <LoadingIndicator variant="medium" />
        </Column>
      );
    }
    if (evaluationsQuery.isError) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <ErrorText errorMessage="Error retrieving candidacy evaluations." />
        </Column>
      );
    }
    if (!evaluations || evaluations.length === 0) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <EmptyState
            description="Start the evaluation of this candidate by clicking Create Evaluation!"
            handlePrimaryClick={() => {
              mixpanelUtils.trackButtonClick(
                "Create Evaluation",
                MIXPANEL_SCREENS.CANDIDATE_SHEET,
              );
              handleCreateEvaluation(jobEvaluations?.[0]);
            }}
            Icon={FileTextIcon}
            PrimaryButtonIcon={PlusIcon}
            primaryButtonName="Create Evaluation"
            title="Create your first evaluation"
            warningMessage={
              !jobEvaluations || jobEvaluations.length === 0
                ? "An evaluation has not been created by the account manager. If you click start evaluation, it will create the default evaluation."
                : undefined
            }
          />
        </Column>
      );
    }

    return (
      <Column className={cn("gap-6 py-7", contentPaddingClassName)}>
        {evaluations.map((evaluation) => (
          <OldEvaluationForm
            key={evaluation.id}
            candidacyId={candidacyId}
            evaluation={evaluation}
            evaluations={evaluations}
          />
        ))}
      </Column>
    );
  };

  return render();
};
